<script setup>
import { computed, ref, shallowRef } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import emojiGroups from 'shared/components/emoji/emojisGroup.json';

defineProps({
  hideLabel: {
    type: Boolean,
    default: false,
  },
  fieldLabel: {
    type: String,
    default: '',
  },
});

const SLUG_ICONS = {
  smileys_emotion: 'i-lucide-smile-plus',
  people_body: 'i-lucide-user',
  animals_nature: 'i-lucide-leaf',
  food_drink: 'i-lucide-utensils',
  travel_places: 'i-lucide-car',
  activities: 'i-lucide-zap',
  objects: 'i-lucide-lightbulb',
  symbols: 'i-lucide-hash',
  flags: 'i-lucide-flag',
};

const { t } = useI18n();

const modelValue = defineModel({
  type: String,
  default: '',
});

const open = ref(false);
const searchQuery = ref('');
const activeSlug = ref(emojiGroups[0]?.slug ?? '');

const enrichedItems = shallowRef([]);

function buildEnriched() {
  enrichedItems.value = emojiGroups.flatMap(group =>
    group.emojis.map(em => ({
      emoji: em.emoji,
      name: em.name,
      slug: em.slug,
      groupSlug: group.slug,
      groupName: group.name,
      searchString: `${em.name} ${em.slug}`.toLowerCase(),
    }))
  );
}

buildEnriched();

const categoryTabs = computed(() =>
  emojiGroups.map(g => ({
    slug: g.slug,
    icon: SLUG_ICONS[g.slug] || 'i-lucide-smile',
  }))
);

const searchResults = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];
  return enrichedItems.value
    .filter(i => i.searchString.includes(q))
    .slice(0, 216);
});

const activeGroup = computed(() =>
  emojiGroups.find(g => g.slug === activeSlug.value)
);

function toggle() {
  open.value = !open.value;
  if (open.value) {
    searchQuery.value = '';
    if (!emojiGroups.some(g => g.slug === activeSlug.value)) {
      activeSlug.value = emojiGroups[0]?.slug ?? '';
    }
  }
}

function close() {
  open.value = false;
}

function selectEmoji(character) {
  modelValue.value = character;
  close();
}

function setCategory(slug) {
  activeSlug.value = slug;
  searchQuery.value = '';
}

const hasSearchQuery = computed(() => searchQuery.value.trim().length > 0);
</script>

<template>
  <div
    class="relative flex h-full min-h-10 w-full flex-col"
    :class="hideLabel ? '' : 'gap-1'"
  >
    <span
      v-if="!hideLabel && fieldLabel"
      class="mb-0 text-sm font-medium leading-6 text-n-slate-12"
    >
      {{ fieldLabel }}
    </span>
    <div class="relative flex min-h-10 flex-1 flex-col">
      <button
        type="button"
        class="flex size-full min-h-10 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 shadow-sm outline-none transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
        :aria-label="fieldLabel || t('LABEL_MGMT.FORM.EMOJI.LABEL')"
        @click="toggle"
      >
        <span v-if="modelValue" class="text-2xl leading-none">{{
          modelValue
        }}</span>
        <Icon
          v-else
          icon="i-lucide-smile-plus"
          class="size-5 text-n-slate-10"
        />
      </button>

      <div
        v-if="open"
        v-on-click-outside="close"
        class="absolute z-[200] mt-1 flex w-[min(calc(100vw-2rem),22rem)] max-h-[min(72vh,21rem)] flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-xl ltr:left-0 rtl:right-0"
      >
        <div
          class="flex shrink-0 gap-0.5 overflow-x-auto border-b border-n-weak px-2 pt-2"
          role="tablist"
        >
          <button
            v-for="tab in categoryTabs"
            :key="tab.slug"
            type="button"
            role="tab"
            class="flex shrink-0 flex-col items-center px-2 pb-2 transition-colors"
            :class="
              activeSlug === tab.slug && !searchQuery.trim()
                ? 'border-b-2 border-n-brand text-n-brand'
                : 'border-b-2 border-transparent text-n-slate-10 hover:text-n-slate-12'
            "
            :aria-selected="activeSlug === tab.slug && !searchQuery.trim()"
            @click="setCategory(tab.slug)"
          >
            <Icon :icon="tab.icon" class="size-5" />
          </button>
        </div>

        <div class="shrink-0 border-b border-n-weak px-2 py-2">
          <Input
            v-model="searchQuery"
            type="search"
            size="sm"
            class="group w-full min-w-0 [&>input]:rounded-lg [&>input]:ltr:!pl-9 [&>input]:rtl:!pr-9"
            :placeholder="t('LABEL_MGMT.FORM.EMOJI_PICKER.SEARCH')"
          />
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-2">
          <template v-if="hasSearchQuery">
            <template v-if="searchResults.length">
              <p
                class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              >
                {{ t('LABEL_MGMT.FORM.EMOJI_PICKER.SEARCH_RESULTS') }}
              </p>
              <div class="grid grid-cols-9 gap-1">
                <button
                  v-for="(item, idx) in searchResults"
                  :key="`${item.slug}-${idx}`"
                  type="button"
                  class="flex size-10 items-center justify-center rounded-lg text-2xl leading-none hover:bg-n-alpha-2"
                  :title="item.name"
                  @click="selectEmoji(item.emoji)"
                >
                  {{ item.emoji }}
                </button>
              </div>
            </template>
            <p v-else class="mb-0 text-sm text-n-slate-11">
              {{ t('LABEL_MGMT.FORM.EMOJI_PICKER.NO_MATCH') }}
            </p>
          </template>

          <template v-else-if="activeGroup">
            <p
              class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            >
              {{ activeGroup.name }}
            </p>
            <div class="grid grid-cols-9 gap-1">
              <button
                v-for="(item, idx) in activeGroup.emojis"
                :key="`${item.slug}-${idx}`"
                type="button"
                class="flex size-10 items-center justify-center rounded-lg text-2xl leading-none hover:bg-n-alpha-2"
                :title="item.name"
                @click="selectEmoji(item.emoji)"
              >
                {{ item.emoji }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
